import { useCallback, useEffect, useRef, useState } from "react"

import { useQuery } from "@tanstack/react-query"

import { toast } from "@/components/ui/cn/toast"
import { IS_DEV } from "@/lib/constants"
import { SchemaSnapshot } from "@/src/graphql/schemaCompatibility"
import { fetchSchemaCompatibility } from "@/src/graphql/schemaCompatibilityClient"

import { useHeartbeatMutation } from "../graphql/public-generated-rq"

const POLLING_INTERVAL = 5 * 60 * 1000 // 5 minutes

export const AppPoller = () => {
  const toastRef = useRef<string | number | null>(null)
  const [enabled, setEnabled] = useState(true)

  const showVersionToast = useCallback(() => {
    if (toastRef.current) toast.dismiss(toastRef.current)
    toastRef.current = toast.message("New version available! Please refresh the app.", {
      action: {
        label: "Refresh",
        onClick: () => window.location.reload(),
      },
      cancel: null,
      duration: Infinity,
    })
  }, [])

  useEffect(() => {
    function callback() {
      setEnabled(document.visibilityState === "visible")
    }

    document.addEventListener("visibilitychange", callback)
    return () => document.removeEventListener("visibilitychange", callback)
  }, [])

  return (
    <AppPollerComponent
      onDifferentVersion={showVersionToast}
      enabled={enabled && !IS_DEV}
    />
  )
}

const AppPollerComponent = ({
  onDifferentVersion,
  enabled = true,
}: {
  onDifferentVersion: () => void
  enabled: boolean
}) => {
  const baseline = useRef<SchemaSnapshot | null>(null)
  const { data: schema } = useQuery({
    queryKey: ["SchemaCompatibility"],
    queryFn: () => fetchSchemaCompatibility(baseline.current),
    refetchInterval: POLLING_INTERVAL,
    enabled,
  })

  useEffect(() => {
    if (!enabled || !schema) return
    // Keep the tab's original schema: additive deployments must not move its baseline.
    if (!baseline.current) baseline.current = schema.snapshot
    else if (schema.breaking) onDifferentVersion()
  }, [enabled, schema, onDifferentVersion])

  const { mutate: sendHeartbeat } = useHeartbeatMutation()
  useEffect(() => {
    if (!enabled) return
    sendHeartbeat({})
    const interval = setInterval(() => sendHeartbeat({}), POLLING_INTERVAL)
    return () => clearInterval(interval)
  }, [enabled, sendHeartbeat])

  return null
}
